import { createSelector } from "@reduxjs/toolkit";
import { IStoreState } from "../../../../reducers/types";
import { VacationRentalShopCallState } from "../index";
import {
  ITrackingProperties,
  ObfuscatedAddress,
  ViewedVacationRentalDetailsProperties,
  ListingCollectionEnum,
  CorporateTravel,
  IIdLodgings,
  PolicyEnum,
} from "redmond";
import { getViewedVacationRentalListProperties } from "../../../availability/reducer";
import { getVacationRentalsLocation } from "../../../search/reducer/selectors";
import { getSelectedAccount } from "../../../rewards/reducer";
import { getTravelWalletCredit } from "../../../travel-wallet/reducer";
import {
  getAdultsCount,
  getChildrenCount,
  getFromDate,
  getPetsCount,
  getUntilDate,
} from "../../../search/reducer";
import dayjs from "dayjs";
import { isCorpTenant } from "@capone/common";
import { config } from "../../../../api/config";
import queryStringParser from "query-string";

export const getVacationRentalShopSelectedListing = (state: IStoreState) =>
  state.vacationRentalShop.selectedHome;

export const getVacationRentalShopSelectedListingCalendar = (
  state: IStoreState
) => state.vacationRentalShop.selectedHomeCalendar;

export const getVacationRentalShopUnavailableDates = (state: IStoreState) =>
  getVacationRentalShopSelectedListingCalendar(state)
    ?.calendarDays?.filter(
      (calendarDay) =>
        !calendarDay.isAvailable && !calendarDay.isAvailableForCheckout
    )
    .map((calendarDay) => dayjs(calendarDay.date).toDate());

export const getNotifyIfShopAndBookPriceDiffer = (state: IStoreState) =>
  state.vacationRentalShop.vacationRentalNotifyIfShopAndBookPriceDiffer;

export const getVacationRentalShopSelectedListingId = (state: IStoreState) =>
  state.vacationRentalShop.vacationRentalListingId;

export const getVacationRentalShopCallState = (state: IStoreState) =>
  state.vacationRentalShop.vacationRentalShopCallState;

export const getVacationRentalShopCallError = (state: IStoreState) =>
  state.vacationRentalShop.vacationRentalShopCallError;

export const getTabView = (state: IStoreState) =>
  state.vacationRentalShop.tabView;

export const getIsBoosted = (state: IStoreState) => 
 state.vacationRentalShop.isBoosted;

export const getShopOffer = (state: IStoreState) =>
  state.vacationRentalShop.shopOffer;

export const getVacationRentalListPrice = (state: IStoreState) =>
  state.vacationRentalShop.listPrice;

export const getIsLifestyleEligible = (state: IStoreState) =>
  state.vacationRentalShop.isLifestyleEligible;

export const hasVacationRentalShopFailed = createSelector(
  getVacationRentalShopCallState,
  (vrShopCallState) => {
    return vrShopCallState === VacationRentalShopCallState.Failed;
  }
);

export const getViewedVacationRentalDetailsProperties = createSelector(
  getViewedVacationRentalListProperties,
  getSelectedAccount,
  getFromDate,
  getUntilDate,
  getAdultsCount,
  getChildrenCount,
  getPetsCount,
  getVacationRentalShopSelectedListing,
  getIsBoosted,
  getVacationRentalsLocation,
  (
    viewedPremierCollectionListProperties,
    account,
    fromDate,
    untilDate,
    adultsCount,
    childrenCount,
    petsCount,
    listing,
    isBoosted,
    vacationRentalsLocation,
  ): ITrackingProperties<ViewedVacationRentalDetailsProperties> => {
    const {
      selectedLodgingIndex,
    } = queryStringParser.parse(window.location.search);

    const isInPolicy =
      !!listing &&
      "corporateTravel" in listing &&
      !!(listing.corporateTravel as CorporateTravel)?.policyCompliance
        ?.isInPolicy;

    return {
      properties: {
        ...viewedPremierCollectionListProperties.properties,
        check_in_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "",
        check_out_date: untilDate ? dayjs(untilDate).format("YYYY-MM-DD") : "",
        adults_count: adultsCount,
        children_count: childrenCount,
        pets_count: petsCount,
        pets_searched: petsCount,
        advance: dayjs(fromDate).diff(dayjs(), "day"),
        account_type_selected: account?.productDisplayName || "",
        ...listing?.listing.trackingProperties?.properties,
        account_use_type: account?.accountUseType,
        account_allow_rewards_redemption: account?.allowRewardsRedemption,
        number_of_photos: listing?.listing.content.media.length,
        home_city: (
          listing?.listing.content.location.address as ObfuscatedAddress
        )?.city,
        home_host_name: listing?.listing.hostInfo?.hostName,
        home_id: listing?.listingId.id,
        product_type: [
          ListingCollectionEnum.Premier,
          ListingCollectionEnum.Lifestyle,
        ].includes(
          listing?.listing.listingCollection ||
            ListingCollectionEnum.NoCollection
        )
          ? (listing?.listing.listingCollection as string)
          : "",
        home_state: (
          listing?.listing.content.location.address as ObfuscatedAddress
        )?.state,
        has_reviews: !!listing?.listing.reviews?.length,
        google_place_id: (vacationRentalsLocation?.id as IIdLodgings)?.lodgingSelection?.placeId,
        destination_name: (vacationRentalsLocation?.id as IIdLodgings)?.lodgingSelection?.searchTerm,
        lodging_row_index: typeof selectedLodgingIndex === "string"
          ? parseInt(selectedLodgingIndex, 10)
          : undefined,
        free_cancellation_shown:
          listing?.availability?.rate?.cancellationPolicy?.policy?.Policy ===
          PolicyEnum.FullyRefundable,
        ...(listing?.listing.content.userRating?.score
          ? {
              reviews_score: Number(
                (listing?.listing.content.userRating?.score / 10).toFixed(1)
              ),
            }
          : {}),
        ...(isCorpTenant(config.TENANT) && {
          in_policy: isInPolicy,
        }),
        ...(isBoosted && { is_boosted: isBoosted }),
      },
      encryptedProperties: [
        ...viewedPremierCollectionListProperties.encryptedProperties,
        listing?.listing.trackingProperties?.encryptedProperties ?? "",
      ],
    };
  }
);

// Travel Wallet
export const showOfferBasedOnSelectedListingSelector = createSelector(
  getShopOffer,
  getTravelWalletCredit,
  (shopOffer, credit): boolean => {
    return (
      !!shopOffer &&
      ((!!credit &&
        Math.abs(credit.amount.amount) === Math.abs(shopOffer.amount.amount)) ||
        !!shopOffer.availabilityPageBanner)
    );
  }
);
